import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { Curious } from '@curi/react';
import __isEmpty from 'lodash/isEmpty';
import { gtmPageviewEvent } from '../../services/google-tag-manager';
import './About.css';
import PageHeader from '../../components/global/PageHeader';

const imgURL = process.env.REACT_APP_CLOUDFRONT_URL;

class About extends React.PureComponent {
  componentDidMount() {
    gtmPageviewEvent({
      pageName: 'codespark.com: About',
      componentProps: this.props,
    });
  }

  goToAccounts(e) {
    // need to check if there are any url params and then make sure those get passed through in the links
    if (!__isEmpty(this.props.response.location.query)) {
      const params = this.props.response.location.query;
      this.props.goToLink(e, `${process.env.REACT_APP_SIGNUP_BASE_URL}=plan&${params}`);
    } else {
      this.props.goToLink(e, process.env.REACT_APP_SIGNUP_BASE_URL);
    }
  }

  render() {
    return (
      <div className="about-page page">
        <Helmet>
          <title>About us | codeSpark</title>
          <meta name="description" content="codeSpark’s mission is to make computer science education accessible to kids everywhere." />
          <meta name="keywords" content="Kids coding languages, Hour of code, coding for kids, kids apps, kids games, learn to code, STEM, Reading, Math, Elementary school" />
          <meta property="og:title" content="Coding Game for Kids - Try it Free!" />
          <meta property="og:description" content="Try the #1 learn-to-code app for kids ages 3-10. Used by over 20 million kids worldwide, kids learn coding basics with puzzles and build skills to make their own games." />
          <meta name="twitter:card" content="app" />
          <meta name="twitter:site" content="@codesparkapp" />
          <meta name="twitter:description" content="Try the #1 learn-to-code app for kids 4+. Used by over 20 Million kids, codeSpark teaches coding basics through creative play and game creation. Coding improves STEM, reading, and math skills." />
          <meta name="twitter:app:country" content="US" />
          <meta name="twitter:app:id:iphone" content="923441570" />
          <meta name="twitter:app:id:ipad" content="923441570" />
          <meta name="twitter:app:id:googleplay" content="org.codespark.thefoos" />
        </Helmet>
        <PageHeader text="About Us" />
        <div className="grid-x grid-padding-x align-center intro-section">
          <div className="small-12 large-9 cell">
            <img src={`${imgURL}/flagship/about/about-header.jpg`} alt="" />
          </div>
        </div>
        <div id="mission-value-props" className="section mission-value">
          <h2>We’re making critical skills accessible for kids everywhere</h2>
          <div className="value-prop-container grid-x grid-padding-x">
            <div className="block small-12 medium-4 cell">
              <img src={`${imgURL}/flagship/home-page/mission-interface.png`} alt="Mission Value" />
              <h3>Intuitive, word-free interface</h3>
              <p>Our patented technology lets kids learn to code before they even know how to read</p>
            </div>
            <div className="block small-12 medium-4 cell">
              <img src={`${imgURL}/flagship/home-page/mission-kids.png`} alt="Mission Value" />
              <h3>Perfect for girls and boys</h3>
              <p>Carefully designed to increase engagement in girls and close the gender gap in STEM</p>
            </div>
            <div className="block small-12 medium-4 cell">
              <img src={`${imgURL}/flagship/home-page/mission-school.png`} alt="Mission Value" />
              <h3>Subsidized for schools</h3>
              <p>Home subscriptions help us fund and integrate computer science in low-income schools</p>
            </div>
          </div>
          <div className="cta-button">
            <button
              data-event-category="button"
              data-event-action="homepage-mission-value-props"
              data-event-label="Help support our mission"
              data-on="click"
              onClick={(e) => this.goToAccounts(e)}
            >
              Help support our mission
              <img className="basic-arrow" src={`${imgURL}/global/icons/basic_arrow_white.svg`} alt="arrow" />
            </button>
          </div>
        </div>
        <div className="grid-x grid-padding-x align-center believe-section">
          <div className="small-12 cell believe-header">
            <h1 className="sub-header alt-blue">What We Believe</h1>
          </div>
          <div className="small-12 cell believe-points">
            <div className="believe-point-single">
              <img src={`${imgURL}/flagship/about/believe_1.png`} alt="" />
              <p className="title">The Magic of Ingenuity</p>
              <p className="description">
                We think the pursuit of ingenious solutions brings together a perfect blend
                of invention, creativity and originality.
              </p>
            </div>
            <div className="believe-point-single">
              <img src={`${imgURL}/flagship/about/believe_2.png`} alt="" />
              <p className="title">The Power of Confidence</p>
              <p className="description">
                We foster the confidence it takes to stretch limits, persevere and stay curious.
                We all benefit from knowing how to be fearless learners.
              </p>
            </div>
            <div className="believe-point-single">
              <img src={`${imgURL}/flagship/about/believe_3.png`} alt="" />
              <p className="title">The Freedom to Experiment</p>
              <p className="description">
                We invite constant exploration and embrace the what ifs. We admire willingness to try
                and believe having permission to fail is liberating.
              </p>
            </div>
            <div className="believe-point-single">
              <img src={`${imgURL}/flagship/about/believe_4.png`} alt="" />
              <p className="title">The Wonder of Childhood</p>
              <p className="description">
                Wonder, curiosity and silliness are baked in to everything we make.<br />Kid-ness is goodness
              </p>
            </div>
            <div className="believe-point-single">
              <img src={`${imgURL}/flagship/about/believe_5.png`} alt="" />
              <p className="title">The Power of Raising the Bar</p>
              <p className="description">
                We think high expectations are a sign of respect, so we approach kids as the
                sophisticated thinkers they&rsquo;re proven to be.
              </p>
            </div>
          </div>
        </div>
        <div className="grid-x grid-padding-x align-center team-section">
          <div className="small-12 cell team-header">
            <h1 className="sub-header">Located in sunny Pasadena, California</h1>
          </div>
          <div className="small-12 cell team-text">
            <img src={`${imgURL}/flagship/about/team-photo.jpg`} alt="" />
            <p>
              codeSpark’s mission is to help all kids learn to code by igniting their curiosity in computer science and turning programming into play. Our award-winning app is designed to teach kids 5 and up the foundations of computer science through puzzles, coding challenges, and creative tools.
            </p>
            <p>
              We believe that to create the best learn-to-code app for all kids, it is important to foster a creative and innovative environment by building a diverse team that reflects the many communities we serve. As part of this effort, we are a participant in Kapor Capital’s Founders’ Commitment to diverse and inclusive hiring and are proud to report that 50% of our team is women and 50% is people of color.
            </p>
            <p>
              codeSpark is also proud to be part of the HOMER family, a growing team dedicated to helping kids everywhere get the best start to their learning journey and build critical skills for the future.
            </p>
            <p>
              Interested in joining the codeSpark team and helping us nurture the next generation of programmers, creators, and leaders? Explore our job openings!
            </p>
            <a href="https://learnwithhomer.com/careers" target="_blank" rel="noopener noreferrer">Explore codeSpark Careers</a>
          </div>
        </div>
        <div className="grid-x grid-padding-x align-center founders-section">
          <div className="small-12 cell founders-header">
            <h1 className="sub-header">Our Founders</h1>
          </div>
          <div className="small-12 cell founders-points">
            <div className="founders-point-single">
              <img src={`${imgURL}/flagship/about/founder_grant.png`} alt="" />
              <p className="title">Grant Hosford</p>
              <p className="subtitle">CEO and Co-Founder</p>
              <p className="description">
                Grant is an experienced entrepreneur
                who has spent the last 15 years
                building successful digital businesses
                in Seattle and Los Angeles.
                Grant has deep expertise in new
                product launches, has lived on 4 continents
                and comes from a family of educators.
                He’s a husband, father of three kids,
                coaches girls soccer and rides bikes often.
              </p>
            </div>
            <div className="founders-point-single">
              <img src={`${imgURL}/flagship/about/founder_joe.png`} alt="" />
              <p className="title">Joe Shochet</p>
              <p className="subtitle">CPO and Co-Founder</p>
              <p className="description">
                Joe has been building world-class and
                award winning children’s games
                for over 20 years. He helped create the
                first version of Alice3D, still in use at
                over 300 universities for CS 101
                and led the creation of ToonTown Online
                for Disney. He’s a husband, father of three kids,
                a LEGO Mindstorm coach
                and a competitive soccer player.
              </p>
            </div>
          </div>
        </div>
        <div className="grid-x grid-padding-x align-center advisors-section">
          <div className="small-12 cell advisors-header">
            <h1 className="sub-header">Our Advisors</h1>
          </div>
          <div className="small-12 cell advisors-points">
            <div className="advisors-point-single">
              <img src={`${imgURL}/flagship/about/advisor_daniel.png`} alt="" />
              <p className="title">Daniel Leyzberg</p>
              <p className="description">
                Daniel received his Ph.D in
                computer science from Yale.
                He is currently a Professor of
                Computer Science
                at Princeton University.
              </p>
            </div>
            <div className="advisors-point-single">
              <img src={`${imgURL}/flagship/about/advisor_coleen.png`} alt="" />
              <p className="title">Coleen Lewis</p>
              <p className="description">
                Colleen Lewis, Ph.D., specializes in
                computer science education
                and curriculum development.
                She is an Assistant Professor of
                Computer Science
                at Harvey Mudd College.
              </p>
            </div>
            <div className="advisors-point-single">
              <img src={`${imgURL}/flagship/about/advisor_kent.png`} alt="" />
              <p className="title">Kent Steen</p>
              <p className="description">
                Kent received his Ph.D. from the University of Nebraska-Lincoln. He is a retired school teacher and district administrator. Kent led his school district to being named the Code.org/CSTA "Champions for Computer Science" in 2018.
              </p>
            </div>
          </div>
        </div>
        <div className="grid-x grid-padding-x align-center meet-begin-section">
          <div className="small-12 cell">
            <h1 className="sub-header">Meet Begin, the Company Behind codeSpark</h1>
          </div>
          <div className="small-12 cell team-text">
            <p>
              Begin is the award-winning early learning company offering children the best start possible through digital, physical, and experiential learning programs. With play-based products including codeSpark, HOMER, KidPass, and Little Passports, Begin builds the skills that matter most to help kids achieve their fullest potential in school and life.
            </p>
            <p>
              Begin is backed by the most recognized names in early childhood development, including LEGO Ventures, Sesame Workshop, and Gymboree Play & Music. For more information about Begin and its suite of integrated programs, visit <a href="https://www.beginlearning.com/" target="_blank">www.beginlearning.com</a>. 
            </p>
          </div>
        </div>
        <div className="grid-x grid-padding-x align-center get-in-touch">
          <div className="small-12 cell">
            <img src={`${imgURL}/flagship/about/all_foos.jpg`} alt="" />
            <h1>Want to get in touch?</h1>
            <a href="/contact">Contact Us</a>
          </div>
        </div>
      </div>
    );
  }
}

About.contextTypes = {
  mixpanel: PropTypes.object
};

export default props => (
  <Curious>
    {({ router, response, navigation }) => {
      return <About router={ router } response={ response } { ...props } />;
    }}
  </Curious>
);



// WEBPACK FOOTER //
// ./src/containers/About/index.js